import {
  UPDATE_LINE_ITEM_REVIEWED,
  UPDATE_MANY_LINE_ITEMS,
} from "@/apis/gql/apolloClient";
import type { LineItem } from "@/apis/gql/types";
import {
  ApolloError,
  type DocumentNode,
  gql,
  useMutation,
} from "@apollo/client";
import PQueue from "p-queue";
import { toast } from "sonner";

export const useUpdateManyLineItems = (options?: {
  refetchQueries?: DocumentNode[];
  disableOptimisticResponse?: boolean;
}) => {
  const [updateManyLineItems] = useMutation(UPDATE_MANY_LINE_ITEMS, {
    ...options,
    update: (cache, { data: mutationResult }) => {
      if (
        !mutationResult?.updateManyLineItems ||
        options?.disableOptimisticResponse
      )
        return;

      mutationResult.updateManyLineItems.forEach((item: any) => {
        const cacheId = `LineItem:${JSON.stringify({
          lineItemId: item.lineItem.lineItemId,
        })}`;
        const existingLineItem = cache.readFragment({
          id: cacheId,
          fragment: gql`
            fragment LineItemReadFields on LineItem {
              lineItemId
              accountReviewed
              taxCodeId
              unlinked
              accountId
            }
          `,
        });

        if (existingLineItem) {
          cache.writeFragment({
            id: cacheId,
            fragment: gql`
              fragment LineItemWriteFields on LineItem {
                accountReviewed
                taxCodeId
                unlinked
                accountId
              }
            `,
            data: {
              ...existingLineItem,
              ...item.lineItem,
            },
          });
        }
      });
    },
    optimisticResponse: ({ lineItemIds, updatedFields }: any) => {
      if (options?.disableOptimisticResponse) return undefined;

      return {
        __typename: "Mutation",
        updateManyLineItems: lineItemIds.map((id: string) => ({
          __typename: "LineItemDetails",
          lineItemDetailsId: id,
          lineItem: {
            __typename: "LineItem",
            lineItemId: id,
            ...updatedFields,
          },
        })),
      };
    },
  });

  return async (
    lineItemIds: string[],
    updatedFields: Partial<
      | LineItem
      | {
          taxCodeId: string | null;
          accountId: string | null;
        }
    >,
  ) => {
    return updateManyLineItems({ variables: { lineItemIds, updatedFields } });
  };
};

const mutationQueue = new PQueue({ concurrency: 1 });

export const useUpdateLineItemReviewed = (options?: {
  refetchQueries?: DocumentNode[];
}) => {
  const [updateLineItemReviewed] = useMutation(UPDATE_LINE_ITEM_REVIEWED, {
    refetchQueries: options?.refetchQueries,
    update: (cache, { data: mutationResult }) => {
      if (!mutationResult?.updateManyLineItems) return;

      mutationResult.updateManyLineItems.forEach((item: any) => {
        const cacheId = `LineItem:${JSON.stringify({
          lineItemId: item.lineItem.lineItemId,
        })}`;
        const existingLineItem = cache.readFragment({
          id: cacheId,
          fragment: gql`
            fragment LineItemReadFields on LineItem {
              lineItemId
              accountReviewed
            }
          `,
        });

        if (existingLineItem) {
          cache.writeFragment({
            id: cacheId,
            fragment: gql`
              fragment LineItemWriteFields on LineItem {
                accountReviewed
              }
            `,
            data: {
              ...existingLineItem,
              ...item.lineItem,
            },
          });
        }
      });
    },

    optimisticResponse: ({ lineItemIds, updatedFields }: any) => ({
      __typename: "Mutation",
      updateManyLineItems: lineItemIds.map((id: string) => ({
        __typename: "LineItemDetails",
        lineItemDetailsId: id,
        lineItem: {
          __typename: "LineItem",
          lineItemId: id,
          ...updatedFields,
        },
      })),
    }),
  });

  return async (
    lineItemIds: string[],
    updatedFields: Partial<
      | LineItem
      | {
          taxCodeId: string | null;
          accountReviewed: boolean;
        }
    >,
    {
      onUndo,
      onSuccess,
    }: {
      onUndo?: () => void;
      onSuccess?: () => void;
    },
  ) => {
    const controller = new AbortController();

    toast(
      `${lineItemIds?.length} Posten ${
        updatedFields.accountReviewed ? "geprüft" : "ungeprüft"
      } markiert`,
      {
        action: {
          label: "Rückgängig",
          onClick: () => {
            controller.abort();

            updateLineItemReviewed({
              variables: {
                lineItemIds,
                updatedFields: {
                  accountReviewed: !updatedFields.accountReviewed,
                },
              },
            });

            onUndo?.();
          },
        },
        position: "top-right",
      },
    );

    mutationQueue.add(async () => {
      try {
        const result = await updateLineItemReviewed({
          variables: {
            lineItemIds,
            updatedFields,
          },
          context: {
            fetchOptions: {
              signal: controller.signal,
            },
          },
        });

        await onSuccess?.();
        return result;
      } catch (error) {
        if (
          error instanceof ApolloError &&
          error.networkError &&
          (error.networkError as any).name === "AbortError"
        ) {
          return;
        }
        throw error;
      }
    });
  };
};
